import { Box, Stack, StackProps, SxProps, Typography, TypographyProps } from '@mui/material';
import { CustomCellRendererProps } from 'ag-grid-react';
import { FC } from 'react';
import { IFinancialsRowData2 } from '../hooks/useFinancialsGridData';

import { useAtomValue } from 'jotai';
import { DescriptionIcon } from '../../../../../components/AgTable/cell-renderers/FieldNameRenderer';
import { Indicator } from '../../../../../components/Indicator/Indicator';
import { KpiConfigPeriod } from '../../../../../data-models/company-financials.data-model';
import { colors } from '../../../../../theme/colors';
import { FMT } from '../../../../../util/formatter-service';
import { IValuesBySection } from '../hooks/useFinancialsColumnDefs';
import { financialsFormatterOverridesAtom } from '../hooks/FinancialsKpiDisplayState';
import { selectedFinancialsCurrencyForIdAtom } from '../hooks/FinancialsCurrencyState';
import { selectedCompanyIdProfileAtom } from '../../../state/UIStateJ';
import { getCurrencyAwareFormatterModel } from '../utils/getCurrencyAwareFormatterModel';
import { getRowCount } from '../hooks/useFinancialsGridRowHeight';
import { PinnedYearColumnTag } from '../pinnedYears/usePinnedYearColDefs';
import { IFinancialsDisplayOptions } from './FinancialsSettingsState';

function InnerRow({ children, sx, ...rest }: TypographyProps) {
  return (
    <Typography {...rest} px={'1rem'} height='100%' marginBlock={'auto'} sx={{ ...sx, lineHeight: 'unset' }}>
      {children}
    </Typography>
  );
}
const highlightedRow: SxProps = {
  background: `${colors.neutral[10]}55`,
};

export const CURRENCY_UNRESOLVED_TITLE = 'FX rate unavailable for the selected currency';

/** Show a muted dash instead of a wrong number when the BE couldn't convert the value. */
function renderFinancialValue(formatted: string, isUnresolved?: boolean) {
  if (!isUnresolved) {
    return formatted;
  }
  return (
    <Box component='span' color='text.disabled' title={CURRENCY_UNRESOLVED_TITLE}>
      —
    </Box>
  );
}
export interface IKpiValueRendererParams<TData, TValue>
  extends CustomCellRendererProps<TData, TValue>, IFinancialsDisplayOptions {}

export const KpiValueRenderer: FC<IKpiValueRendererParams<IFinancialsRowData2, IValuesBySection<unknown>>> = (
  params
) => {
  const { value: valuesBySection, showBudget, showGrowth, showLTM, showYTD } = params;
  const formatterOverrides = useAtomValue(financialsFormatterOverridesAtom);
  const companyId = useAtomValue(selectedCompanyIdProfileAtom);
  const selectedCurrency = useAtomValue(selectedFinancialsCurrencyForIdAtom(companyId));
  if (!valuesBySection) {
    return <></>;
  }

  const nRows = getRowCount({ showBudget, showLTM, showYTD });

  const overrideId = params.data ? formatterOverrides[params.data.entityField] : undefined;
  const formatter = overrideId
    ? FMT.get().getFormatterForId(overrideId)
    : params.data
      ? FMT.get().getFormatterForModel(getCurrencyAwareFormatterModel(params.data, selectedCurrency))
      : null;

  const formattedActual =
    formatter && valuesBySection.actual != null ? formatter(valuesBySection.actual) : '';

  const formattedBudget =
    formatter && valuesBySection.budget != null ? formatter(valuesBySection.budget) : '';

  const growth = valuesBySection.growth;
  const isPinnedYearColumn = params?.column?.getColId()?.includes(PinnedYearColumnTag);
  const formattedLtm =
    !isPinnedYearColumn && formatter && valuesBySection[KpiConfigPeriod.LTM] !== null
      ? formatter(valuesBySection[KpiConfigPeriod.LTM])
      : '';
  const formattedYtd =
    formatter && valuesBySection[KpiConfigPeriod.CYTD] !== null
      ? formatter(valuesBySection[KpiConfigPeriod.CYTD])
      : '';

  const unresolved =
    (valuesBySection as { currencyUnresolved?: Partial<Record<string, boolean>> }).currencyUnresolved ?? {};

  return (
    <ValuesBySectionContainer nRows={nRows}>
      <InnerRow
        display={'grid'}
        gridTemplateColumns={'repeat(2, minMax(0, 1fr))'}
        alignItems={'center'}
        justifyContent={'space-between'}
      >
        {showGrowth && typeof growth === 'number' ? (
          <Indicator indicator={growth ?? 1} style={{ justifySelf: 'start', lineHeight: '1.5rem' }} />
        ) : (
          <span />
        )}
        <Typography variant='monotype'>{renderFinancialValue(formattedActual, unresolved.actual)}</Typography>
      </InnerRow>

      {showBudget && (
        <InnerRow variant='monotype' color='text.secondary'>
          {renderFinancialValue(formattedBudget, unresolved.budget)}
        </InnerRow>
      )}

      {showLTM && (
        <InnerRow variant='monotype' color='text.secondary' sx={highlightedRow} className='highlightedRow'>
          {renderFinancialValue(formattedLtm, unresolved[KpiConfigPeriod.LTM])}
        </InnerRow>
      )}

      {showYTD && (
        <InnerRow variant='monotype' color='text.secondary' sx={highlightedRow}>
          {renderFinancialValue(formattedYtd, unresolved[KpiConfigPeriod.CYTD])}
        </InnerRow>
      )}
    </ValuesBySectionContainer>
  );
};

export const KpiNameCellRenderer: FC<IKpiValueRendererParams<IFinancialsRowData2, string>> = (params) => {
  const { value, showBudget, showLTM, showYTD, data } = params;
  const nRows = getRowCount({ showBudget, showLTM, showYTD });

  if (!value) {
    return <></>;
  }
  return (
    <ValuesBySectionContainer
      justifyContent={'start'}
      justifyItems={'start'}
      alignItems={'center'}
      nRows={nRows}
      lineHeight={'100%'}
      height={'100%'}
    >
      <InnerRow variant='body2' noWrap display={'flex'} alignItems={'center'} gap={2}>
        {value} <DescriptionIcon description={data?.description ?? null} />
      </InnerRow>
      {showBudget && (
        <InnerRow variant='body2' color={'text.secondary'}>
          Budget
        </InnerRow>
      )}
      {showLTM && (
        <InnerRow variant='body2' color={'text.secondary'} sx={highlightedRow}>
          LTM
        </InnerRow>
      )}
      {showYTD && (
        <InnerRow variant='body2' color={'text.secondary'} sx={highlightedRow}>
          YTD
        </InnerRow>
      )}
    </ValuesBySectionContainer>
  );
};

interface IValuesBySectionContainerProps extends StackProps {
  nRows: number;
}
export const ValuesBySectionContainer: FC<IValuesBySectionContainerProps> = ({ children, nRows }) => {
  return (
    <Stack
      display='grid'
      gridTemplateRows={`repeat(${nRows}, minmax(0, 1fr))`}
      justifyContent={'stretch'}
      alignItems={'center'}
      alignContent={'center'}
      height='100%'
      minWidth={'100%'}
    >
      {children}
    </Stack>
  );
};
